import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePlaces } from "../context/PlacesContext";
import { SectionLabel } from "../components/Bits";

export default function Guide() {
  const { guides, placesFromSlugs, loading } = usePlaces();

  if (loading) {
    return <div className="pt-[140px] pb-40 text-center text-ink-soft font-serif text-2xl">Loading…</div>;
  }

  const available = (guides || [])
    .map((g) => ({ ...g, count: placesFromSlugs(g.placeSlugs || []).length }))
    .filter((g) => g.count > 0);

  if (!available.length) {
    return <div className="pt-[140px] pb-40 text-center font-serif text-3xl">Guides coming soon.</div>;
  }
  const [lead, ...rest] = available;

  return (
    <div className="pt-[68px]" data-testid="guides-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <SectionLabel>The Guide</SectionLabel>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-semibold mt-4 display-balance max-w-3xl">
          Curated lists from people who actually go.
        </h1>
        <p className="text-ink-soft mt-5 max-w-xl text-lg">
          Not an algorithm, not a popularity contest — considered recommendations built from real visits.
        </p>

        <Link to={`/guide/${lead.slug}`} data-testid="guide-lead-card" className="group grid lg:grid-cols-2 gap-10 mt-14 items-center">
          <div className="img-zoom relative aspect-[4/3] bg-ink/5">
            <img src={lead.cover} alt={lead.title} className="w-full h-full object-cover" />
            <span className="absolute top-5 left-5 eyebrow text-paper text-[10px]">{lead.count} places</span>
          </div>
          <div>
            <span className="eyebrow text-claret text-[10px]">Editor's pick</span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-2 group-hover:text-claret transition-colors display-balance">{lead.title}</h2>
            <p className="text-ink-soft mt-5 text-lg leading-relaxed">{lead.dek}</p>
            <p className="text-sm text-ink-soft mt-4">{lead.author} · {lead.date}</p>
            <span className="inline-flex items-center gap-2 mt-6 text-sm font-semibold link-underline">
              Read the guide <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 mt-20">
          {rest.map((g, i) => (
            <Link key={g.id} to={`/guide/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group rise-in" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="img-zoom relative aspect-[3/2] bg-ink/5">
                <img src={g.cover} alt={g.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
                <span className="absolute top-4 left-4 eyebrow text-paper text-[10px]">{g.count} places</span>
              </div>
              <h3 className="font-serif text-2xl font-semibold mt-4 group-hover:text-claret transition-colors">{g.title}</h3>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed line-clamp-2">{g.dek}</p>
              <p className="text-xs text-ink-soft/70 mt-3">{g.author} · {g.date}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
